import { api } from './apiClient';
import { User } from '../context/AuthContext';

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  user: User;
  token: string;
}

interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  department: string;
  role: string;
}

// Map backend snake_case user fields to frontend camelCase User type
const mapUser = (raw: any): User => ({
  id: raw.id,
  email: raw.email,
  name: raw.full_name || raw.name || '',
  role: raw.role || '',
  department: raw.department || '',
  phone: raw.phone || '',
});

export const authService = {
  // Login user
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<any>('/auth/login', credentials);
    return {
      ...response,
      user: mapUser(response.user),
    };
  },

  // Register new user
  register: async (userData: RegisterRequest): Promise<LoginResponse> => {
    const response = await api.post<any>('/auth/register', userData);
    return {
      ...response,
      user: mapUser(response.user),
    };
  },

  // Get current user profile
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<{ user: any }>('/auth/me');
    return mapUser(response.user);
  },

  // Logout user
  logout: async (sessionId?: number): Promise<void> => {
    try {
      await api.post('/auth/logout', { sessionId });
    } catch (error) {
      // Ignore logout errors
      console.warn('Logout error:', error);
    }
  },

  // Refresh token
  refreshToken: async (): Promise<string> => {
    const response = await api.post<{ token: string }>('/auth/refresh');
    return response.token;
  },

  // Update user profile
  updateProfile: async (userData: Partial<User>): Promise<User> => {
    // Controller nests the result under a `user` key (see
    // src/controllers/auth.js updateProfile).
    const response = await api.put<{ user: User }>('/auth/profile', userData);
    return response.user;
  },

  // Change password
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await api.put('/auth/password', {
      currentPassword,
      newPassword,
    });
  },

  // Request password reset
  requestPasswordReset: async (email: string): Promise<void> => {
    await api.post('/auth/forgot-password', { email });
  },

  // Reset password. Body keys are `password` + `confirmPassword` (not
  // `newPassword`) — matches validateResetPassword in src/validators/auth.js,
  // which requires both and checks they're equal server-side too. The caller
  // is expected to have already confirmed the two inputs match.
  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await api.post('/auth/reset-password', { token, password: newPassword, confirmPassword: newPassword });
  },
};